import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useParams, useLocation, Link } from 'react-router-dom'
import { useDispatch, connect } from 'react-redux'
import qs from 'qs'
import { throttle } from 'lodash'
import Loader from '~components/Loader'
import Product from '~components/shop/Product'
import { loadRangeProducts, loadSizeMap, setSizingMode } from '~redux/shop'
import Select from '~components/ui/Select'
import RefineBy from '~components/RefineBy'
import Swatch from '~components/ui/Swatch'
import Legend from '~components/shop/Legend'
import FilterView from '~components/ui/FilterView'
import { isScreenSize } from '~helpers/mediaQuery'
import { capitaliseFirstLetter } from '~helpers/content'
import Parse from 'html-react-parser'
import MediaQuery from '~components/helpers/MediaQuery'
import classNames from 'classnames'
import { pageView } from '~helpers/ga'
import axios from 'axios'

const ShopRange = ({ brands, products, colourSwatches, sizeMap, sizingMode, rangeDetails }) => {
  const [selectedColour, setSelectedColour] = useState(null)
  const [styleFilter, setStyleFilter] = useState('')
  const [listView, setListView] = useState(true)
  const [matrixOverlay, setMatrixOverlay] = useState(false)
  const [product, setProduct] = useState(null)

  const dispatch = useDispatch()
  const params = useParams()
  const location = useLocation()
  const rangeProducts = products[params.range]
  const rangeProductDetails = rangeDetails[params.range]

  useEffect(() => {
    pageView()
    const handleResize = throttle(() => {
      if (!isScreenSize('lg')) {
        setListView(false)
      }
    }, 100)

    window.addEventListener('resize', handleResize)
    handleResize()

    dispatch(loadSizeMap())
    dispatch(loadRangeProducts(params.range))
      .then(() => setDefaultColour())

    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [params.range, location.search, rangeProducts])

  const setDefaultColour = () => {
    const colours = getColours()
    const query = qs.parse(location.search, { ignoreQueryPrefix: true })
    let defaultColour = colours[0]?.code || null

    if (query.colour && colours.some(c => c.code === query.colour)) {
      defaultColour = query.colour
    }

    setSelectedColour(defaultColour)
  }

  const getColours = () => {
    if (!rangeProducts) return []

    return rangeProducts.reduce((carry, product) => {
      const colour = product.custom_fields.colour_code
      const colourName = product.custom_fields.colour

      if (!carry.find(c => c.code === colour)) {
        carry.push({ code: colour, name: colourName })
      }

      return carry
    }, [])
  }

  const getBrand = () => {
    if (rangeProducts && rangeProducts.length > 0) {
      const brandId = rangeProducts[0].brand_id

      if (brandId) {
        return brands.find(b => b.id === brandId)
      }
    }

    return null
  }

  const handleColourChange = (colour) => {
    setSelectedColour(colour)
  }

  const handleSizingModeChange = (e) => {
    const { value } = e.target
    axios.post('/api/account/store-size', { size: value })
    dispatch(setSizingMode(value))
  }

  const switchView = () => {
    setListView(!listView)
  }

  const toggleMatrixOverlay = (product) => {
    setMatrixOverlay(!matrixOverlay)
    setProduct(product)
    // Temporary until permanent solution found
    document.body.classList.toggle('overflow-y-hidden')
  }

  const getCategoryImage = (description = '') => {
    const images = description.match(/<img[^>]+>/gi)
    return images && images.length ? Parse(images[0]) : null
  }

  const displayMessage = (description) => {
    const image = getCategoryImage(description)
    return image
      ? (
        <MediaQuery breakpoint="md">
          {image}
        </MediaQuery>
        )
      : (
        <div className="block px-4 py-3 rounded bg-grey editor editor-standard">
          {Parse(description)}
        </div>
        )
  }

  // Render logic remains mostly the same as in the class component
  if (!rangeProducts || !brands) {
    return <Loader />
  }

  const styles = rangeProducts.reduce((carry, product) => {
    if (!carry.find(i => i.name === product.custom_fields.style)) {
      carry.push({
        name: product.custom_fields.style,
        value: product.custom_fields.style
      })
    }

    return carry
  }, [])

  styles.sort()

  const styleProducts = rangeProducts.filter(product => {
    if (styleFilter && product.custom_fields.style !== styleFilter) {
      return false
    }

    return product.custom_fields.colour_code === selectedColour
  })

  const sizingOptions = Object.keys(sizeMap.bra.bands[0]).map(s => ({
    name: s.toUpperCase().replace(/GB/g, 'UK').replace(/DE/g, 'EU'),
    value: s
  }))

  const breadcrumbs = [{
    title: 'Shop',
    url: '/shop'
  },
  {
    title: getBrand()?.name,
    url: `/shop/brands/${getBrand()?.slug}`
  }]

  return (
    <>
      <div className="w-full mt-2 lg:mt-10">
        <div className="px-4 mx-auto xxl:container md:px-8 xxl:px-0">
          <div className="pb-4 border-b border-coral xxl:pb-8">
            {breadcrumbs && (
              <div className="inline-flex mb-3 md:mb-2">
                {breadcrumbs.map((breadcrumb, index) => (
                  <span key={index}>
                    {breadcrumb.url && (
                      <Link className="mx-1 hover:underline focus:underline" to={breadcrumb.url}>
                        {capitaliseFirstLetter(breadcrumb.title)}
                      </Link>
                    )}

                    {!breadcrumb.url && (
                      <span className="mx-1">
                        {capitaliseFirstLetter(breadcrumb.title)}
                      </span>
                    )}

                    {index !== breadcrumbs.length - 1 && (
                      <span className="inline">
                        /
                      </span>
                    )}
                  </span>
                ))}
              </div>
            )}

            <div className={classNames(
              'flex flex-col items-start lg:flex-row lg:items-center lg:justify-between'
            )}
            >
              <div className={classNames(
                'w-full',
                { 'sm:w-auto': rangeProductDetails?.description },
                { 'flex-wrap flex items-center justify-between': !rangeProductDetails?.description }
              )}
              >
                <h1 className={classNames(
                  'text-3xl m-0 md:text-4xl xxl:text-7xl',
                  { 'lg:mr-6 xl:mr-10': !rangeProductDetails?.description }
                )}
                >
                  {rangeProductDetails ? rangeProductDetails.name : params.range}
                </h1>

                <div className={classNames(
                  'w-full flex mt-3',
                  { 'sm:w-auto lg:mt-0': !rangeProductDetails?.description }
                )}
                >
                  <div className="flex -m-1 xl:-m-2">
                    <MediaQuery type="max" breakpoint="lg">
                      <div className="px-1 my-1 xl:px-2 xl:my-2">
                        <RefineBy
                          text="Refine by"
                          colours={getColours()}
                          selectedColour={selectedColour}
                          onColourChange={(colourCode) => handleColourChange(colourCode)}
                          sizingModes={sizingOptions}
                          selectedSizingMode={sizingMode}
                          onSizingModeChange={handleSizingModeChange.bind(this)}
                          className="w-full"
                        />
                      </div>
                    </MediaQuery>

                    <MediaQuery breakpoint="lg">
                      <div className="px-1 my-1 xl:px-2 xl:my-2">
                        <FilterView
                          className="w-auto"
                          onClick={(e) => switchView(e)}
                          listView={listView}
                        />
                      </div>

                      <div className="px-1 my-1 xl:px-2 xl:my-2">
                        <Select
                          items={sizingOptions}
                          className="w-auto"
                          value={sizingMode}
                          onChange={handleSizingModeChange.bind(this)}
                        />
                      </div>
                    </MediaQuery>
                  </div>
                </div>
              </div>

              {rangeDetails?.description && (
                <div className="mt-4 lg:max-w-1/2 lg:items-end lg:mt-0 lg:ml-6 xl:ml-10">
                  {displayMessage(rangeDetails.description)}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="mt-4 md:mt-6">
        <div className="px-4 mx-auto xxl:container md:px-8 xxl:px-0">
          <div className="flex-wrap items-center justify-between hidden -mx-2 -mb-2 lg:flex lg:-mx-3 lg:mt-6 xl:mt-8">
            <Legend />

            <div className="w-auto px-2 my-2 lg:px-3">
              <div className="flex items-center">
                <span className="block mr-4 xl:mr-6">
                  Colours:
                </span>

                <div className="flex items-center -m-1">
                  {getColours().map(colour => (
                    <Swatch
                      key={colour.code}
                      colour={colour.code}
                      name={colour.name}
                      swatch={colourSwatches[colour.code]}
                      checked={selectedColour === colour.code}
                      onChange={handleColourChange.bind(this, colour.code)}
                      className="px-1 my-1"
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="w-full mt-8 mb-20">
        <div className="w-full px-4 mx-auto overflow-hidden xxl:container md:px-8 xxl:px-0">
          <div className="flex flex-wrap -mx-2 -my-5 lg:-mx-3 xl:-mx-5">
            {styleProducts.map((product, index) => (
              <Product
                key={index}
                product={product}
                range={params.range}
                colours={getColours()}
                selectedColour={selectedColour}
                index={index}
                listView={listView}
                toggleMatrixOverlay={(individualProduct) => toggleMatrixOverlay(individualProduct)}
              />
            ))}
          </div>
        </div>
      </div>
    </>
  )
}

ShopRange.propTypes = {
  products: PropTypes.object,
  brands: PropTypes.array,
  colourSwatches: PropTypes.object,
  sizeMap: PropTypes.object,
  sizingMode: PropTypes.string,
  rangeDetails: PropTypes.object,
  dispatch: PropTypes.func,
  loadSize: PropTypes.func,
  setSizingMode: PropTypes.func
}

const mapStateToProps = (state, ownProps) => {
  return {
    ...ownProps,
    brands: state.shop.brands,
    products: state.shop.products,
    colourSwatches: state.shop.colourSwatches,
    sizeMap: state.shop.sizeMap,
    sizingMode: state.shop.sizingMode,
    rangeDetails: state.shop.rangeDetails
  }
}

export default connect(mapStateToProps)(ShopRange)
